import React, { useState } from 'react';
import ServiceLayout from '../components/ServiceLayout.jsx';
import { Quote, Plus, X, Phone, Calendar, ArrowRight, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const SymptomAccordion = ({ items }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
      {items.map((item, index) => (
        <div key={item.title} className="border-b border-[#E8DDCE]">
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full py-6 flex justify-between items-center text-left group cursor-pointer"
          >
            <h3 className="text-xl md:text-2xl font-serif text-[#3B352F] group-hover:text-[#123F13] transition-colors">
              {item.title}
            </h3>
            <div className="text-[#123F13] transition-transform duration-300">
              {openIndex === index ? <X size={24} /> : <Plus size={24} />}
            </div>
          </button>
          <AnimatePresence>
            {openIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="pb-8 pr-4">
                  <p className="text-[#3B352F]/70 leading-relaxed text-base md:text-lg">
                    {item.content}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

const Traume = () => {
  const canonicalUrl = "https://corehypnose.dk/behandlinger/traume";
  
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Traumebehandling med Hypnose og Metakognitiv Terapi",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Core Hypnose v/ Lars Nissen Corell – Psykoterapeut MPF",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Valby Tingsted 7",
        "addressLocality": "Valby",
        "postalCode": "2500",
        "addressCountry": "DK"
      },
      "telephone": "+4527204671"
    },
    "description": "Få ro på nervesystemet og frihed fra overvældende tanker og flashbacks med professionel traumebehandling i Valby og online. Over 26 års erfaring.",
    "areaServed": "Danmark",
    "serviceType": "Psykoterapi"
  };

  return (
    <ServiceLayout 
      title="Traumebehandling - Få ro på nervesystemet"
      subtitle="Få frihed fra overvældende tanker og flashbacks. Hypnose og metakognitiv terapi hjælper dig med at finde tryghed i kroppen igen og genskabe følelsen af kontrol."
      image="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/a2e1a858-83cb-4ea4-b3b5-0a17ac535be6.png"
      description="Søger du en tryg vej ud af traumets greb? Hos Core hypnose hjælper jeg dig med at skabe ro – uden at du behøver at genopleve det hele."
      actions={
        <>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#193f10] text-white px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#193f10]/90 transition-all shadow-md shrink-0">
            <Calendar size={18} /> Book første behandlingssession
          </Link>
          <a href="tel:27204671" className="inline-flex items-center gap-2 border-2 border-[#193f10] text-[#193f10] px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#faf7f2] transition-all shrink-0">
            <Phone size={18} /> Tlf. 27 20 46 71
          </a>
        </>
      }
    >
      <Helmet>
        <title>Traumebehandling i Valby & Online | Slip fri af flashbacks hos CoreHypnose</title>
        <meta name="description" content="Professionel traumebehandling med hypnose og metakognitiv terapi. Få ro på nervesystemet og slip for flashbacks og uro. Lars Nissen Corell – Psykoterapeut MPF med 26+ års erfaring." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Traumebehandling i Valby & Online | CoreHypnose" />
        <meta property="og:description" content="Find tryghed i kroppen igen med effektiv traumebehandling. Slip for det ubevidste pres og genskab kontrollen over dit liv." />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      {/* Case-studie */}
      <div className="bg-[#123F13] text-white p-8 md:p-12 rounded-2xl relative overflow-hidden mb-16">
        <Quote className="absolute top-8 right-8 text-white/10" size={120} />
        <div className="relative z-10 max-w-3xl">
          <h3 className="text-2xl font-serif mb-6 italic">“Jeg kunne endelig finde ro, selv når alt var stille”</h3>
          <p className="text-white/80 mb-6 leading-relaxed text-lg">
            Henriette på 22 år kom efter en trafikulykke. Selvom hun ikke kom fysisk til skade, fandt hendes nervesystem aldrig ro igen. Hun var konstant på vagt, undgik at køre bil og sov dårligt.
          </p>
          <p className="text-white/80 mb-6 leading-relaxed text-lg">
            Gennem metakognitive teknikker og hypnose lærte Henriette at give slip på det mentale alarmberedskab. Hun begyndte at sove bedre, genoptog bilkørsel og følte endelig, at hun fik sit liv tilbage uden at skulle gennemleve ulykken igen og igen.
          </p>
          <p className="font-bold">— Henriette, 22 år</p>
        </div>
      </div>

      {/* Hvad er et traume sektion */}
      <section className="mb-16">
        <div className="w-full">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-6">Hvad er et traume – og hvordan føles det?</h2>
          <div className="space-y-6 text-[#3B352F]/70 leading-relaxed text-lg">
            <p>
              Et traume er ikke bare en voldsom oplevelse. Det er det aftryk, som oplevelsen efterlader i din krop og dit sind, længe efter det hele “burde være overstået”.
            </p>
            <p>
              Selv situationer, som “ikke burde” føles traumatiske, kan sætte sig som dybe spor, hvis du følte dig magtesløs, alene eller overvældet. Det er ikke dig, der overreagerer. Det er din hjerne og krop, der forsøger at beskytte dig, men har sat sig fast i overlevelses-mode.
            </p>
            
            <div className="bg-[#FAF7F2] p-8 rounded-2xl border border-[#E8DDCE] mt-8">
              <h3 className="text-xl font-bold text-[#123F13] mb-4">Hvordan føles et uforløst traume?</h3>
              <p className="mb-4">Det varierer fra person til person, men mange beskriver en grundfølelse af at:</p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "“Noget stadig sidder i kroppen”",
                  "“Tankerne kører i ring” (især ved triggers)",
                  "“Jeg bliver overvældet uden grund”",
                  "Flashbacks eller mareridt",
                  "Uforklarlig uro eller følelsesudbrud",
                  "Følelsen af at være “fastlåst” i fortiden"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Symptom Accordion */}
          <div className="py-16 border-t border-[#E8DDCE] mt-16">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="text-[#8C2F12] text-xs font-semibold tracking-[0.15em] uppercase mb-4 block">Symptomer</span>
              <h2 className="text-4xl md:text-5xl font-serif text-[#123F13] mb-6">Typiske tegn på uforløste traumer</h2>
              <p className="text-[#3B352F]/70 text-lg">
                Et traume påvirker ikke kun hukommelsen, det kan sætte sig i hele nervesystemet. Her er de mest almindelige tegn.
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              <SymptomAccordion items={[
                {
                  title: "Kropslig uro",
                  content: "Konstant uro eller anspændthed i kroppen, som om du altid er i alarmberedskab. Det kan føles som en indre sitren eller en tunghed, der aldrig helt forsvinder."
                },
                {
                  title: "Flashbacks",
                  content: "Uønskede billeder, lyde eller følelser fra den traumatiske oplevelse, der pludselig dukker op og får det til at føles, som om det sker igen her og nu."
                },
                {
                  title: "Undgåelsesadfærd",
                  content: "Du begynder at undgå bestemte steder, personer, situationer eller samtaler, der minder dig om det skete, for at beskytte dig selv mod ubehag."
                },
                {
                  title: "Søvnproblemer",
                  content: "Svært ved at falde til ro, hyppige opvågninger eller mareridt, der gør natten til en kamp i stedet for en kilde til hvile."
                },
                {
                  title: "Overreaktioner",
                  content: "Du reagerer kraftigere end normalt på lyde, stemninger eller berøringer. Nervesystemet er blevet overfølsomt og tolker små ting som potentielle farer."
                },
                {
                  title: "Følelsesløshed",
                  content: "En følelse af at være “afkoblet” fra dig selv eller andre. Det kan føles som om, du ser dit liv udefra, eller at dine følelser er blevet bedøvede."
                }
              ]} />
            </div>
          </div>
        </div>
      </section>

      {/* Hvorfor bliver det ved sektion */}
      <section className="bg-[#FAF7F2] p-8 md:p-12 rounded-2xl border border-[#E8DDCE] mb-24">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-serif text-[#123F13] mb-6">Hvorfor bliver traumet ved med at fylde?</h2>
          <div className="space-y-4 text-[#3B352F]/70 leading-relaxed text-lg">
            <p>
              Det er ikke traumet i sig selv, men hvordan du forholder dig til det bagefter, der ofte fastholder reaktionen. Når hjernen forsøger at forstå, analysere og “løse” det, der skete gennem grublerier og selvovervågning, bliver nervesystemet holdt fanget i alarmberedskab.
            </p>
            <p className="font-medium text-[#123F13]">
              Min tilgang hjælper dig med at bryde dette mønster. Jeg arbejder ikke med at overanalysere fortiden, men med at lære dit sind at regulere sig selv igen.
            </p>
          </div>
        </div>
      </section>

      {/* Behandlingsforløb */}
      <section className="py-16 border-t border-[#E8DDCE] mb-24">
        <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-12">Sådan foregår traumebehandlingen</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="space-y-10">
            {[
              { title: "Forstå dine reaktioner", content: "Jeg afmystificerer dine symptomer, så du forstår, hvorfor din krop reagerer som den gør. Det skaber det første fundament af ro." },
              { title: "Metakognitive teknikker", content: "Du lærer at lade grublerier og selvfokus ligge, hvilket giver sindet plads til at hele sig selv naturligt." },
              { title: "Hypnose og ro i kroppen", content: "Jeg bruger hypnose til at berolige nervesystemet direkte og hjælpe dig med at give slip på det indre alarmberedskab." },
              { title: "Enkle værktøjer", content: "Du får konkrete redskaber til hverdagen, så du kan håndtere triggere og uro, når de opstår." },
              { title: "Varig forandring", content: "Sammen lægger vi en plan for, hvordan du holder fast i din nye ro og styrke, så du kan se fremad med tillid." }
            ].map((step, i) => (
              <div key={i} className="flex gap-6">
                <div className="flex-shrink-0 w-10 h-10 bg-[#123F13] text-white rounded-full flex items-center justify-center font-bold">
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#123F13] mb-2">{step.title}</h3>
                  <p className="text-[#3B352F]/70 text-lg leading-relaxed">{step.content}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white p-8 rounded-2xl border border-[#E8DDCE] shadow-sm h-fit sticky top-24">
            <h3 className="text-2xl font-serif text-[#123F13] mb-6">Derfor virker behandlingen</h3>
            <ul className="space-y-6">
              <li className="flex gap-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#123F13]/10 flex items-center justify-center text-[#123F13]">
                  <ArrowRight size={14} />
                </div>
                <p className="text-[#3B352F]/70"><strong>Bryd den onde cirkel:</strong> Jeg hjælper dig med at stoppe den konstante genbearbejdning af oplevelsen.</p>
              </li>
              <li className="flex gap-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#123F13]/10 flex items-center justify-center text-[#123F13]">
                  <ArrowRight size={14} />
                </div>
                <p className="text-[#3B352F]/70"><strong>Fokus på nuet:</strong> Du får redskaber til de udfordringer, du mærker lige her og nu.</p>
              </li>
              <li className="flex gap-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#123F13]/10 flex items-center justify-center text-[#123F13]">
                  <ArrowRight size={14} />
                </div>
                <p className="text-[#3B352F]/70"><strong>Nænsom proces:</strong> Du skal ikke genopleve traumet eller fortælle alt i detaljer.</p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <div className="text-center pb-16">
        <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-4">Klar til at få ro og kontrol igen?</h2>
        <p className="text-[#3B352F]/70 mb-8 text-lg">Jeg tilbyder behandling i klinikken i Valby eller online – vælg det, der føles tryggest for dig.</p>
        <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#8C2F12] text-white px-8 py-4 rounded-md font-bold text-lg hover:bg-[#a33a1a] transition-all shadow-lg">
          Book din første session nu <ArrowRight size={20} />
        </Link>
        <div className="mt-8">
          <a 
            href="https://www.dr.dk/sundhed/psykologi/traumer" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#3B352F]/50 hover:text-[#8C2F12] transition-colors text-sm"
          >
            Læs mere om traumer og psykologi <ExternalLink size={14} />
          </a>
        </div>
      </div>
    </ServiceLayout>
  );
};

export default Traume;
